import { useQuery } from "@tanstack/react-query";
import { fetchGeoRun, fetchGeoSnapshot } from "@/lib/api";
import { Lock, AlertTriangle } from "lucide-react";
import PromptResponsesBlock from "./PromptResponsesBlock";

const SENTIMENT_TONE = {
  positive: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Positivo" },
  neutral: { bg: "bg-slate-50", text: "text-slate-600", label: "Neutro" },
  negative: { bg: "bg-rose-50", text: "text-rose-700", label: "Negativo" },
  mixed: { bg: "bg-amber-50", text: "text-amber-700", label: "Mixto" },
};
const ENGINE_LABEL = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
};

export default function BrandPerceptionPanel({ clientId, runId, snapshotRunId }) {
  const runQ = useQuery({
    queryKey: ["geo-run", clientId, runId],
    queryFn: () => fetchGeoRun(clientId, runId),
    enabled: !!runId,
  });
  const snapQ = useQuery({
    queryKey: ["geo-snapshot", clientId],
    queryFn: () => fetchGeoSnapshot(clientId),
  });

  if (!runId) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 p-10 text-center bg-slate-50/50" data-testid="geo-brand-empty">
        <p className="text-sm text-slate-600">
          Sin runs ejecutadas. Ejecutá un análisis para ver cómo te describen las IA.
        </p>
      </div>
    );
  }
  if (runQ.isLoading) {
    return <div className="rounded-md border border-slate-200 p-6 text-sm text-slate-500">Cargando percepción…</div>;
  }

  const brandRows = (runQ.data?.results || []).filter((r) => r.prompt_type === "brand");

  if (!brandRows.length) {
    return (
      <div className="rounded-md border border-slate-200 p-6 text-sm text-slate-500" data-testid="geo-brand-empty-no-brand-prompts">
        Esta run no tiene prompts de tipo <strong>marca</strong>. Asegurate de que las 4 plantillas de marca estén activas en <strong>Prompts y configuración</strong>.
      </div>
    );
  }

  const isSnapshotRun = !snapshotRunId || snapshotRunId === runId;

  // Group brand rows by prompt → one PromptResponsesBlock per prompt.
  const byPrompt = new Map();
  for (const r of brandRows) {
    if (!byPrompt.has(r.prompt_text)) {
      byPrompt.set(r.prompt_text, {
        prompt: { text: r.prompt_text, prompt_type: "brand" },
        rows: [],
      });
    }
    byPrompt.get(r.prompt_text).rows.push(r);
  }
  const blocks = [...byPrompt.values()];

  // Aggregate sentiment + errors across all brand rows.
  const sentiments = { positive: 0, neutral: 0, negative: 0, mixed: 0 };
  const errors = [];
  for (const r of brandRows) {
    const s = r.structured || {};
    const sent = (s.sentiment || "").toLowerCase();
    if (sentiments[sent] != null) sentiments[sent] += 1;
    for (const e of s.factual_errors || []) {
      if (e) errors.push({ text: e, engine: r.engine, prompt: r.prompt_text });
    }
  }

  return (
    <div className="space-y-6" data-testid="geo-brand-panel">
      {isSnapshotRun && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 inline-flex items-center gap-2">
          <Lock className="h-4 w-4" />
          Esta run es el <strong>Snapshot Día 0</strong> — punto de referencia para futuros análisis.
        </div>
      )}

      {/* SENTIMENT BREAKDOWN */}
      <section>
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">
          Sentimiento global
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(sentiments).map(([k, v]) => {
            const tone = SENTIMENT_TONE[k];
            return (
              <div
                key={k}
                className={`rounded-md border border-slate-200 ${tone.bg} p-3`}
                data-testid={`geo-sentiment-${k}`}
              >
                <div className={`text-[11px] uppercase tracking-wider font-medium ${tone.text}`}>
                  {tone.label}
                </div>
                <div className="font-display text-2xl font-semibold text-slate-900 mt-1">
                  {v}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FACTUAL ERRORS — surface prominently for action */}
      {errors.length > 0 && (
        <section>
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Posibles errores factuales detectados
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Afirmaciones que la IA hizo sobre la marca que podrían ser inexactas. Revisalas manualmente.
          </p>
          <div className="rounded-md border border-amber-200 bg-amber-50/50 divide-y divide-amber-100">
            {errors.map((e, i) => (
              <div key={i} className="px-3 py-2.5 text-sm text-slate-700">
                <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mr-2">
                  {ENGINE_LABEL[e.engine] || e.engine}
                </span>
                {e.text}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FULL RESPONSES PER BRAND PROMPT */}
      <section>
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">
          Respuestas completas — cómo te ve cada IA
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Por cada prompt de marca, la respuesta literal de cada motor con sus citas, atributos asociados y posibles errores factuales. Click para expandir.
        </p>
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <PromptResponsesBlock
              key={i}
              prompt={b.prompt}
              rows={b.rows}
              defaultOpen={blocks.length <= 2}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
